/**
 * @fileoverview Connect application icon requests to the managed visual vocabulary.
 *
 * `icons/phosphorIconManager.js` owns aliases, legacy Prime mappings, and default presentation. This adapter accepts managed names, legacy strings, or supplied React elements so screens can migrate without separate icon systems. Icon meaning and accessible surrounding labels belong to the caller.
 */

import React from 'react'
import {
    phosphorIconSettings,
    resolveLegacyPrimeIcon,
    resolveManagedIcon,
} from './icons/phosphorIconManager'
import './AppIcon.css'

const LEGACY_PRIME_UTILITY_CLASSES = new Set(['pi', 'pi-fw', 'pi-spin'])



function classNames(...values) {
    return values
        .flatMap(value => String(value || '').split(/\s+/))
        .map(value => value.trim())
        .filter(Boolean)
        .join(' ')
}



function parseIconString(icon) {
    const classes = String(icon || '').split(/\s+/).filter(Boolean)
    const legacyIconClass = classes.find(className => (
        className.toLowerCase().startsWith('pi-') &&
        !LEGACY_PRIME_UTILITY_CLASSES.has(className.toLowerCase())
    ))

    const utilityClassName = classes
        .filter(className => {
            const normalized = className.toLowerCase()
            return !LEGACY_PRIME_UTILITY_CLASSES.has(normalized) && !normalized.startsWith('pi-')
        })
        .join(' ')

    return {
        legacyIconClass,
        utilityClassName,
        spin: classes.some(className => className.toLowerCase() === 'pi-spin'),
    }
}

/**
 * Resolve presentation identity for string-based callers. React elements bypass registry resolution and are rendered directly by AppIcon; unknown strings use the managed fallback rather than defining a new icon.
 */
export function resolveAppIcon(icon) {
    if (!icon || React.isValidElement(icon)) {
        return null
    }

    if (typeof icon !== 'string') {
        return null
    }

    const directManagedIcon = resolveManagedIcon(icon)
    const parsedIcon = parseIconString(icon)
    const resolvedIcon = directManagedIcon || resolveLegacyPrimeIcon(parsedIcon.legacyIconClass)

    return {
        IconComponent: resolvedIcon?.icon || phosphorIconSettings.fallbackIcon,
        weight: resolvedIcon?.weight,
        utilityClassName: parsedIcon.utilityClassName,
        spin: parsedIcon.spin,
    }
}



function AppIcon({
    icon,
    className,
    size,
    weight,
    mirrored,
    color,
    spin,
    style,
    'aria-hidden': ariaHidden,
    ...rest
}) {
    if (React.isValidElement(icon)) {
        return icon
    }

    const resolvedIcon = resolveAppIcon(icon)

    if (!resolvedIcon) {
        return null
    }

    const {
        IconComponent,
        weight: resolvedWeight,
        utilityClassName,
        spin: iconShouldSpin,
    } = resolvedIcon

    return (
        <IconComponent
            aria-hidden={ariaHidden ?? 'true'}
            color={color}
            focusable="false"
            mirrored={mirrored ?? phosphorIconSettings.mirrored}
            size={size ?? phosphorIconSettings.size}
            weight={weight ?? resolvedWeight ?? phosphorIconSettings.weight}
            className={classNames(
                phosphorIconSettings.className,
                utilityClassName,
                className,
                (spin || iconShouldSpin) && phosphorIconSettings.spinClassName
            )}
            style={style}
            {...rest}
        />
    )
}

export default AppIcon
